import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Image as ImageIcon,
  Camera,
  Eraser,
  Gift,
  Coins,
  Moon,
  Sun,
  Menu,
  X,
  ArrowRight,
  LogIn,
  LogOut,
  User,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PaymentModal from '../components/PaymentModal';
import AccessModal from '../components/AccessModal';
import DiscountRoulette from '../components/DiscountRoulette';
import {
  trackViewContent,
  trackInitiateCheckout,
  trackPurchase,
} from '../utils/metaPixel';

const FUNNEL_PACKAGES = [
  {
    id: 5,
    tokens: 50,
    price: 20.0,
    product_hash: 'tokens_50',
    title: '50 tokens',
  },
  {
    id: 6,
    tokens: 375,
    price: 50.0,
    product_hash: 'tokens_375',
    title: '375 tokens',
    popular: true,
  },
  {
    id: 7,
    tokens: 500,
    price: 75.0,
    product_hash: 'tokens_500',
    title: '500 tokens',
  },
];

const LandingFunnel = () => {
  const { user, loading, isLoggedIn, hasPaidAccess, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [dark, setDark] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showRoulette, setShowRoulette] = useState(false);
  const [discountReais, setDiscountReais] = useState(0);
  const [rouletteOffered, setRouletteOffered] = useState(false);

  const displayPackages = FUNNEL_PACKAGES.map((pkg) => ({
    ...pkg,
    originalPrice: pkg.price,
    price: Math.max(1, Number((pkg.price - discountReais).toFixed(2))),
  }));

  useEffect(() => {
    trackViewContent('landing_funnel', 'page', { content_name: 'Landing Funnel' });
  }, []);

  useEffect(() => {
    if (!loading && hasPaidAccess) {
      navigate('/app', { replace: true });
    }
  }, [loading, hasPaidAccess, navigate]);

  const pickFile = (f) => {
    if (!f || !f.type.startsWith('image/')) return;
    if (f.size > 5 * 1024 * 1024) {
      alert('Imagem até 5MB.');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    pickFile(f);
  };

  const handleRemoveClothes = () => {
    if (!file) return;
    if (!hasPaidAccess) {
      setShowPlans(true);
      return;
    }
    navigate('/app');
  };

  const offerRouletteIfNeeded = () => {
    if (!rouletteOffered && discountReais <= 0) {
      setRouletteOffered(true);
      setShowRoulette(true);
    }
  };

  const closePlansWithoutBuy = () => {
    setShowPlans(false);
    offerRouletteIfNeeded();
  };

  const closePaymentWithoutBuy = () => {
    setShowPayment(false);
    setSelectedPackage(null);
    offerRouletteIfNeeded();
  };

  const openBuy = (pkg) => {
    trackInitiateCheckout(pkg.price, 'BRL', [`tokens_${pkg.tokens}`]);
    setSelectedPackage({
      ...pkg,
      title: discountReais > 0 ? `${pkg.title} (−R$ ${discountReais})` : pkg.title,
    });
    setShowPlans(false);
    setShowPayment(true);
  };

  const handleRouletteWon = (value) => {
    setDiscountReais(value);
    setShowRoulette(false);
    setShowPlans(true);
  };

  const handlePaymentSuccess = async (transaction) => {
    if (selectedPackage) {
      trackPurchase(selectedPackage.price, 'BRL', [`tokens_${selectedPackage.tokens}`]);
    }
    const email =
      transaction?.customer_email ||
      transaction?.customer?.email ||
      '';
    // Evita roleta no onClose automático após sucesso
    setRouletteOffered(true);
    setShowPayment(false);
    setSelectedPackage(null);

    if (isLoggedIn) {
      await refreshUser();
      navigate('/app', { replace: true });
      return;
    }

    // Convidado: após 1ª compra → cadastro (mesmo email do PIX)
    const q = new URLSearchParams({ from: 'payment' });
    if (email) q.set('email', email);
    navigate(`/register?${q.toString()}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fff0f5]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-pink-400 border-t-transparent" />
      </div>
    );
  }

  const bg = dark ? 'bg-zinc-950 text-white' : 'bg-[#fff0f5] text-zinc-800';
  const card = dark
    ? 'bg-zinc-900 border-zinc-700'
    : 'bg-white border-pink-200/80';
  const muted = dark ? 'text-zinc-400' : 'text-zinc-500';
  const pink = 'text-pink-500';

  return (
    <div className={`min-h-screen ${bg} transition-colors`}>
      {/* Header */}
      <header className="mx-auto flex max-w-lg items-center justify-between px-4 py-4">
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`flex h-11 w-11 items-center justify-center rounded-2xl ${card} border shadow-sm`}
          aria-label="Borracha de Roupa"
        >
          <Eraser className={`h-5 w-5 ${pink}`} />
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPlans(true)}
            className={`flex h-11 w-11 items-center justify-center rounded-2xl ${card} border shadow-sm`}
            aria-label="Baú de bônus"
          >
            <Gift className={`h-5 w-5 ${pink}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowPlans(true)}
            className={`inline-flex h-11 items-center gap-1.5 rounded-full border px-3 text-sm font-medium shadow-sm ${card}`}
          >
            <Coins className={`h-4 w-4 ${pink}`} />
            {user?.tokens ?? 0} tokens
          </button>
          <button
            type="button"
            onClick={() => setDark((d) => !d)}
            className={`flex h-11 w-11 items-center justify-center rounded-2xl ${card} border shadow-sm`}
            aria-label={dark ? 'Tema claro' : 'Tema escuro'}
          >
            {dark ? <Sun className={`h-5 w-5 ${pink}`} /> : <Moon className={`h-5 w-5 ${pink}`} />}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className={`flex h-11 w-11 items-center justify-center rounded-2xl ${card} border shadow-sm`}
            aria-label="Menu"
          >
            {menuOpen ? <X className={`h-5 w-5 ${pink}`} /> : <Menu className={`h-5 w-5 ${pink}`} />}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div className={`mx-auto mb-2 max-w-lg px-4`}>
          <div className={`rounded-2xl border p-3 shadow-sm ${card}`}>
            {isLoggedIn ? (
              <div className="space-y-1">
                <p className={`px-2 py-1 text-sm ${muted}`}>
                  <User className="mr-1 inline h-3.5 w-3.5" />
                  {user?.nome || user?.email}
                </p>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-pink-500/10"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowPlans(true);
                  }}
                >
                  <Coins className="h-4 w-4 text-pink-500" /> Comprar tokens
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm hover:bg-pink-500/10"
                  onClick={() => {
                    logout();
                    setMenuOpen(false);
                  }}
                >
                  <LogOut className="h-4 w-4 text-pink-500" /> Sair
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <Link
                  to="/login?redirect=/"
                  className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-pink-500/10"
                  onClick={() => setMenuOpen(false)}
                >
                  <LogIn className="h-4 w-4 text-pink-500" /> Entrar
                </Link>
                <Link
                  to="/register?redirect=/"
                  className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-pink-500/10"
                  onClick={() => setMenuOpen(false)}
                >
                  <User className="h-4 w-4 text-pink-500" /> Criar conta
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      <main className="mx-auto max-w-lg px-4 pb-16 pt-2">
        <div className="mb-8 text-center">
          <h1 className={`mb-2 flex items-center justify-center gap-2 text-3xl font-bold ${pink}`}>
            <Eraser className="h-8 w-8" />
            Borracha de Roupa
          </h1>
          <p className={`text-base ${dark ? 'text-zinc-300' : 'text-zinc-600'}`}>
            Remova roupas com apenas um clique!
          </p>
        </div>

        {/* Upload */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mb-4 rounded-3xl border-2 border-dashed p-8 text-center transition-colors ${card} ${
            dragOver ? 'border-pink-500 bg-pink-500/5' : ''
          }`}
        >
          {preview ? (
            <div className="space-y-4">
              <img
                src={preview}
                alt="Preview"
                className="mx-auto max-h-64 rounded-2xl object-contain"
              />
              <button
                type="button"
                onClick={() => {
                  setPreview(null);
                  setFile(null);
                }}
                className={`text-sm ${muted} underline`}
              >
                Trocar imagem
              </button>
            </div>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-pink-500/15">
                <ImageIcon className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="mb-1 text-lg font-semibold">Importe sua foto</h3>
              <p className={`mb-4 text-sm ${muted}`}>
                Clique ou arraste e solte sua imagem aqui
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 text-sm font-medium text-pink-500 hover:text-pink-600"
              >
                <Camera className="h-4 w-4" /> Escolher imagem
              </button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
        </div>

        {/* Resultado “teaser” com blur — só após upload */}
        {preview && (
          <div className="relative mb-4 overflow-hidden rounded-3xl border border-pink-200/80">
            <img
              src={`${process.env.PUBLIC_URL}/img/img2.jpg`}
              alt="Prévia do resultado"
              className="w-full scale-105 object-cover blur-md"
            />
            <div
              className="absolute inset-0 bg-white/30 backdrop-blur-sm"
              aria-hidden
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-black/55 px-4 py-2 text-sm font-semibold text-white shadow">
                Resultado bloqueado — compre tokens
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          disabled={!file}
          onClick={handleRemoveClothes}
          className={`mb-2 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-semibold transition-colors ${
            file
              ? 'bg-pink-500 text-white hover:bg-pink-600'
              : dark
                ? 'cursor-not-allowed bg-zinc-800 text-zinc-500'
                : 'cursor-not-allowed bg-zinc-200 text-zinc-400'
          }`}
        >
          <Eraser className="h-5 w-5" />
          Remover Roupas
          <ArrowRight className="h-5 w-5" />
        </button>
        {!file && (
          <p className={`mb-4 text-center text-sm ${muted}`}>
            Carregue uma imagem para continuar
          </p>
        )}
        {file && <div className="mb-4" />}

        <div className="mb-8 overflow-hidden rounded-3xl">
          <img
            src={`${process.env.PUBLIC_URL}/img/exemplo-landing.jpeg`}
            alt="Exemplo Borracha de Roupa"
            className="w-full object-cover"
          />
        </div>

        <div className={`space-y-3 text-sm leading-relaxed ${muted}`}>
          <p>1. Este recurso é destinado para uso por adultos com 18 anos ou mais.</p>
          <p>
            2. Ao fazer upload de imagens, você confirma que possui os direitos e permissões
            necessários. Você é o único responsável por todo o conteúdo que você faz upload e
            gera.
          </p>
          <p>
            3. As imagens carregadas são usadas exclusivamente para processamento de IA e não
            serão usadas para nenhum outro propósito.
          </p>
          <p>4. Para resultados ótimos, por favor, faça o upload de imagens de alta qualidade (até 5MB).</p>
        </div>

        <p className={`mt-10 text-center text-xs ${muted}`}>
          Este site não é afiliado do facebook ou da rede Meta.
        </p>
      </main>

      {/* Planos modal */}
      {showPlans && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePlansWithoutBuy();
          }}
        >
          <div className={`w-full max-w-md rounded-3xl border p-6 shadow-xl ${card}`}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Compre Tokens</h2>
              <button type="button" onClick={closePlansWithoutBuy} aria-label="Fechar">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className={`mb-4 text-sm ${muted}`}>
              Carregue seus tokens para gerar imagens. Após o 1º pagamento você acessa o app
              completo.
            </p>
            {discountReais > 0 && (
              <p className="mb-3 rounded-xl bg-pink-500/10 px-3 py-2 text-center text-sm font-medium text-pink-600">
                Desconto de R$ {discountReais.toFixed(0)} aplicado nos pacotes
              </p>
            )}
            <div className="space-y-3">
              {displayPackages.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => openBuy(pkg)}
                  className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition-colors hover:border-pink-400 ${
                    pkg.popular ? 'border-pink-400 bg-pink-500/5' : dark ? 'border-zinc-700' : 'border-zinc-200'
                  }`}
                >
                  <div>
                    <p className="font-semibold">{pkg.tokens} tokens</p>
                    {pkg.popular && (
                      <p className="text-xs font-medium text-pink-500">Mais popular</p>
                    )}
                  </div>
                  <span className="text-right font-semibold text-pink-500">
                    {discountReais > 0 && (
                      <span className={`mr-2 text-xs font-normal line-through ${muted}`}>
                        R$ {pkg.originalPrice.toFixed(2)}
                      </span>
                    )}
                    R$ {pkg.price.toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <DiscountRoulette
        isOpen={showRoulette}
        dark={dark}
        onClose={() => setShowRoulette(false)}
        onWon={handleRouletteWon}
      />

      <AccessModal
        isOpen={showAccess}
        onClose={() => setShowAccess(false)}
        title="Crie sua conta"
        message="Faça login ou cadastre-se para comprar tokens e remover roupas."
        redirectTo="/"
      />

      <PaymentModal
        isOpen={showPayment}
        onClose={closePaymentWithoutBuy}
        packageData={selectedPackage}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default LandingFunnel;
